import React, { ChangeEvent, FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { cloneDeep } from "lodash";
import { CalculationMethodEnum, ProviderRegionTypeEnum, ServicingAnalysisTypeEnum, ServicingCountTypeEnum, ServicingMinimumTypeEnum } from "@app/templates/templateModels";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { ClientListItem } from "@app/client/client";
import { AccessCriteriaModel, ServicingCriteriaModel, ServicingDetailsModel, AdequacySpecialtyGroupDisplayPropertiesEnum, AdequacySpecialtyGroupAnalysisPropertiesEnum, processKeyDownNumericValidation, processKeyDownTextValidation, SpecialtyGroupModel, EntityTypeEnum } from "@app/admin/manage-specialties/specialty";
import { CodeMapSetService } from "@app/codemaps";
import { CodeMapTypeEnum } from "@app/codemaps/CodeMapSetModels";
import { InputValidationRegExps } from "@app/projects/enumerators";
import { wafflesKeyColumnName, ColumnConfig } from "@app/shared/waffles";
import { TemplateDetailsData } from "@app/admin/manage-specialties/templateDetailsData";

/* oxlint-disable sort-keys */

interface CreatePanelProps {
  currentUserid: number;
  defaultClient: ClientListItem;
  features: Set<string>;
  handleSave: (
    accessReqInCounty: boolean,
    categoryDisplayName: string,
    creditPercentage: number,
    expandable: boolean,
    isProvider: boolean,
    loading: boolean,
    informational: boolean,
    selectedTaxonomyCrosswalk: string,
    servicingReqInCounty: boolean,
    specialtyAccessCriteria: AccessCriteriaModel[],
    specialtyServicingCriteria: ServicingCriteriaModel[],
    specialtyCode: string,
    specialtyCodeLineItems: SpecialtyCodeLineItem[],
    specialtyCodeName: string,
    specialtyCodeProviderWeight: string,
    specialtyGroupCode: string,
    specialtyGroupCodeName: string,
    taxonomyCode: string,
    taxonomyCrosswalks: JSX.Element[],
    taxonomySpecialtyCode: string,
    taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[],
    telehealthCredit: boolean,
    usingRatios: boolean,
    countDistinct: ServicingCountTypeEnum,
    minimumServicingCounts: number,
    providerCount: number,
    index?: number,
    servicingDetails?: ServicingDetailsModel[],
    customizationDetails?: ServicingDetailsModel[],
  ) => void;
  allowClientEdit: boolean;
  loading: boolean;
  templateDetails: TemplateDetailsData;
  specialtyGroup?: SpecialtyGroupModel;
  countyClassificationMap: number;
  index?: number,
  servicingDetails?: ServicingDetailsModel[],
  customizationDetails?: ServicingDetailsModel[],
  minimumServicingCounts?: number,
  providerCount?: number,
}

interface CreatePanelState {
  accessReqInCounty: boolean;
  templateDetails: TemplateDetailsData;
  categoryDisplayName: string;
  creditPercentage: number;
  creditPercentageIsValid: boolean;
  expandable: boolean;
  isProvider: boolean;
  loading: boolean;
  nameIsValid: boolean;
  nameIsValidText: string;
  informational: boolean;
  selectedTaxonomyCrosswalk: string;
  countyClassificationMap: number;
  servicingReqInCounty: boolean;
  skipAccessTimeAndDistanceCalc: boolean;
  specialtyCode: string,
  specialtyCodeLineItems: SpecialtyCodeLineItem[],
  specialtyCodeName: string;
  specialtyCodeProviderWeight: string;
  specialtyCodeProviderWeightIsValid: boolean;
  specialtyGroupCode: string;
  specialtyGroupCodeIsValid: boolean;
  specialtyGroupCodeName: string;
  specialtyGroupCodeNameIsValid: boolean;
  taxonomyCode: string;
  taxonomyCrosswalks: JSX.Element[];
  taxonomySpecialtyCode: string;
  taxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[];
  telehealthCredit: boolean;
  usingRatios: boolean;

  countDistinct: ServicingCountTypeEnum;
  minimumServicingCounts: string | number;
  providerCount: number;
  servicingDetailsData: ServicingDetailsRow[];
  specialtyAccessCriteria: ServicingDetailsModel[];
  accessCustomizationsData: ServicingDetailsRow[];
  accessCustomizations: ServicingDetailsModel[];
  counties: string[];
  memberCounts: string | number;
  panelIsValid: boolean;
  servicingDetailsErrorMessages: string[];
  highlightErrors: boolean;
  duplicateSpecialtyCodeLineItem: boolean;
}

interface SpecialtyCodeLineItem {
  id: number,
  specialtyCode: string,
  specialtyCodeName: string,
  specialtyCodeProviderWeight: string,
}

interface TaxonomySpecialtyCodeLineItem {
  id: number,
  taxonomyCode: string,
  taxonomySpecialtyCode: string,
}

interface CalculationMethodDropdown {
  value: CalculationMethodEnum;
  displayText: string;
}

interface ServicingDetailsRow {
  geographyId: JSX.Element;
  requiredProviders: JSX.Element;
  miles: JSX.Element;
  minutes: JSX.Element;
  passPercentage: JSX.Element;
  servicingProviders: JSX.Element;
  calculationMethod: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const calculationMethodDropdownValues: CalculationMethodDropdown[] = Object.values(CalculationMethodEnum).map((x) => {
  let displayText = "";
  let value: CalculationMethodEnum;
  switch (x) {
    case CalculationMethodEnum.TimeAndDistance:
      displayText = "Time And Distance";
      value = CalculationMethodEnum.TimeAndDistance;
      break;
    case CalculationMethodEnum.TimeOrDistance:
      displayText = "Time Or Distance";
      value = CalculationMethodEnum.TimeOrDistance;
      break;
    case CalculationMethodEnum.Time:
      displayText = "Time";
      value = CalculationMethodEnum.Time;
      break;
    case CalculationMethodEnum.Distance:
      displayText = "Distance";
      value = CalculationMethodEnum.Distance;
      break;
    default:
      displayText = "";
      value = CalculationMethodEnum.Unknown;
      break;
  }
  return { value: value, displayText: displayText }
});

const CreateNewSpecialtyPanelContent: FC<CreatePanelProps> = (props: CreatePanelProps) => {
  const flyoutContext = useContext(FlyoutContext);

  const initialState = useMemo<CreatePanelState>(() => {
    const defaultSpecialtyGroupInfo = {
      accessReqInCounty: false,
      creditPercentage: 10,
      expandable: false,
      isProvider: false,
      informational: false,
      selectedTaxonomyCrosswalk: '',
      servicingReqInCounty: false,
      skipAccessTimeAndDistanceCalc: false,
      specialtyCode: "",
      specialtyCodeName: "",
      specialtyCodeProviderWeight: "1",
      specialtyGroupCode: "",
      specialtyGroupCodeIsValid: true,
      specialtyGroupCodeName: "",
      specialtyGroupCodeNameIsValid: true,
      telehealthCredit: false,
      usingRatios: false,

      calculationMethod: "",
      urbanCalculationMethod: "",
      suburbanCalculationMethod: "",
      ruralCalculationMethod: "",
      servicingProviders: 0,
      countDistinct: "",
      miles: 0,
      minutes: 0,
      minimumServicingCounts: 0,
      passPercentage: 0,
      providerCount: 0,
      requiredProviders: props.providerCount ?? 0,
    }

    if (props.specialtyGroup === undefined) {
      return {
        accessReqInCounty: defaultSpecialtyGroupInfo.accessReqInCounty,
        templateDetails: props.templateDetails,
        categoryDisplayName: "",
        creditPercentage: defaultSpecialtyGroupInfo.creditPercentage,
        creditPercentageIsValid: false,
        expandable: defaultSpecialtyGroupInfo.expandable,
        isProvider: defaultSpecialtyGroupInfo.isProvider,
        loading: false,
        nameIsValid: defaultSpecialtyGroupInfo.specialtyGroupCode.length > 0,
        nameIsValidText: "",
        informational: defaultSpecialtyGroupInfo.informational,
        selectedTaxonomyCrosswalk: defaultSpecialtyGroupInfo.selectedTaxonomyCrosswalk,
        countyClassificationMap: props.countyClassificationMap,
        servicingReqInCounty: defaultSpecialtyGroupInfo.servicingReqInCounty,
        skipAccessTimeAndDistanceCalc: defaultSpecialtyGroupInfo.skipAccessTimeAndDistanceCalc,
        specialtyCode: defaultSpecialtyGroupInfo.specialtyCode,
        specialtyCodeLineItems: [],
        specialtyCodeName: defaultSpecialtyGroupInfo.specialtyCodeName,
        specialtyCodeProviderWeight: defaultSpecialtyGroupInfo.specialtyCodeProviderWeight,
        specialtyCodeProviderWeightIsValid: true,
        specialtyGroupCode: defaultSpecialtyGroupInfo.specialtyGroupCode,
        specialtyGroupCodeIsValid: defaultSpecialtyGroupInfo.specialtyGroupCodeIsValid,
        specialtyGroupCodeName: defaultSpecialtyGroupInfo.specialtyGroupCodeName,
        specialtyGroupCodeNameIsValid: true,
        taxonomyCode: "",
        taxonomyCrosswalks: [],
        taxonomySpecialtyCode: "",
        taxonomySpecialtyCodeLineItems: [],
        telehealthCredit: defaultSpecialtyGroupInfo.telehealthCredit,
        usingRatios: defaultSpecialtyGroupInfo.usingRatios,

        countDistinct: defaultSpecialtyGroupInfo.countDistinct as ServicingCountTypeEnum,
        minimumServicingCounts: defaultSpecialtyGroupInfo.minimumServicingCounts,
        providerCount: defaultSpecialtyGroupInfo.providerCount,
        servicingDetailsData: [],
        specialtyAccessCriteria: [],
        accessCustomizationsData: [],
        accessCustomizations: [],
        counties: [],
        servicingDetailsErrorMessages: [],
        memberCounts: 0,
        panelIsValid: false,
        highlightErrors: false,
        duplicateSpecialtyCodeLineItem: false,
      };
    } else {
      const specialtyCodeLineItems: SpecialtyCodeLineItem[] = props.specialtyGroup.specialties.map((x) => (
        {
          id: x.displayOrder,
          specialtyCode: x.code,
          specialtyCodeName: x.name,
          specialtyCodeProviderWeight: x.weight
        }
      ))

      return {
        accessReqInCounty: props.specialtyGroup.accessCriteria[0].providerRegionType == ProviderRegionTypeEnum.InRegion ? true : false,
        templateDetails: props.templateDetails,
        categoryDisplayName: "",
        creditPercentage: props.specialtyGroup.accessCriteria[0].telehealthAccessCreditPercentage,
        creditPercentageIsValid: false,
        expandable: props.specialtyGroup.displayProperties == AdequacySpecialtyGroupDisplayPropertiesEnum.CanExpandSpecialties ? true : false,
        isProvider: props.specialtyGroup.entityType == EntityTypeEnum.Provider ? true : false,
        loading: false,
        nameIsValid: props.specialtyGroup.code.length > 0,
        nameIsValidText: "",
        informational: props.specialtyGroup.analysisProperties == AdequacySpecialtyGroupAnalysisPropertiesEnum.InformationalOnly ? true : false,
        selectedTaxonomyCrosswalk: props.templateDetails?.attestationCrosswalkCodeMapSetId.toString(),
        countyClassificationMap: props.countyClassificationMap,
        servicingReqInCounty: props.specialtyGroup.servicingCriteria[0]?.servicingAnalysisType == ServicingAnalysisTypeEnum.InRegion ? true : false,
        skipAccessTimeAndDistanceCalc: props.specialtyGroup.accessCriteria[0].skipAccessTimeAndDistanceCalculations,
        specialtyCode: defaultSpecialtyGroupInfo.specialtyCode, // ?
        specialtyCodeLineItems: specialtyCodeLineItems,
        specialtyCodeName: '',
        specialtyCodeProviderWeight: defaultSpecialtyGroupInfo.specialtyCodeProviderWeight, // ?
        specialtyCodeProviderWeightIsValid: true,
        specialtyGroupCode: props.specialtyGroup.code,
        specialtyGroupCodeIsValid: true,
        specialtyGroupCodeName: props.specialtyGroup.name,
        specialtyGroupCodeNameIsValid: true,
        taxonomyCode: "",
        taxonomyCrosswalks: [],
        taxonomySpecialtyCode: "",
        taxonomySpecialtyCodeLineItems: [],
        telehealthCredit: props.specialtyGroup.accessCriteria[0].isTelehealthAccessCreditCustomization,
        usingRatios: props.specialtyGroup.servicingMinimumType === ServicingMinimumTypeEnum.Ratio ? true : false,
        countDistinct: props.specialtyGroup.servicingCountBy as ServicingCountTypeEnum,
        minimumServicingCounts: props.specialtyGroup.servicingCriteria[0].servicingMinimumCount ?? null,
        providerCount: defaultSpecialtyGroupInfo.providerCount,
        servicingDetailsData: [],
        specialtyAccessCriteria: [],
        accessCustomizationsData: [],
        accessCustomizations: [],
        counties: [],
        memberCounts: 0,
        servicingDetailsErrorMessages: [],
        panelIsValid: false,
        highlightErrors: false,
        duplicateSpecialtyCodeLineItem: false,
      };
    }
  }, []);

  const nameValidationIntervalRef = useRef<number>(null);
  const [accessReqInCounty, setAccessReqInCounty] = useState(initialState.accessReqInCounty);
  /* oxlint-disable-next-line @typescript-eslint/no-unused-vars */
  const [templateDetails, setTemplateDetails] = useState(initialState.templateDetails);
  const [categoryDisplayName, setCategoryDisplayName] = useState(initialState.categoryDisplayName);
  const [creditPercentage, setCreditPercentage] = useState(initialState.creditPercentage);
  const [creditPercentageIsValid, setCreditPercentageIsValid] = useState(initialState.creditPercentageIsValid);
  const [expandable, setExpandable] = useState(initialState.expandable);
  const [isProvider, setIsProvider] = useState(initialState.isProvider);
  const [loading, setLoading] = useState(initialState.loading);
  /* oxlint-disable-next-line @typescript-eslint/no-unused-vars */
  const [nameIsValid, setNameIsValid] = useState(initialState.nameIsValid);
  /* oxlint-disable-next-line @typescript-eslint/no-unused-vars */
  const [nameIsValidText, setNameIsValidText] = useState(initialState.nameIsValidText);
  const [informational, setInformational] = useState(initialState.informational);
  const [selectedTaxonomyCrosswalk, setSelectedTaxonomyCrosswalk] = useState(initialState.selectedTaxonomyCrosswalk);
  /* oxlint-disable-next-line @typescript-eslint/no-unused-vars */
  const [countyClassificationMap, setCountyClassificationMap] = useState(initialState.countyClassificationMap);
  const [servicingReqInCounty, setServicingReqInCounty] = useState(initialState.servicingReqInCounty);
  const [skipAccessTimeAndDistanceCalc, setSkipAccessTimeAndDistanceCalc] = useState(initialState.skipAccessTimeAndDistanceCalc);
  const [specialtyCode, setSpecialtyCode] = useState(initialState.specialtyCode);
  const [specialtyCodeLineItems, setSpecialtyCodeLineItems] = useState(initialState.specialtyCodeLineItems);
  const [specialtyCodeName, setSpecialtyCodeName] = useState(initialState.specialtyCodeName);
  const [specialtyCodeProviderWeight, setSpecialtyCodeProviderWeight] = useState(initialState.specialtyCodeProviderWeight);
  const [specialtyCodeProviderWeightIsValid, setSpecialtyCodeProviderWeightIsValid] = useState(initialState.specialtyCodeProviderWeightIsValid);
  const [specialtyGroupCode, setSpecialtyGroupCode] = useState(initialState.specialtyGroupCode);
  const [specialtyGroupCodeIsValid, setSpecialtyGroupCodeIsValid] = useState(initialState.specialtyGroupCodeIsValid);
  const [specialtyGroupCodeName, setSpecialtyGroupCodeName] = useState(initialState.specialtyGroupCodeName);
  const [specialtyGroupCodeNameIsValid, setSpecialtyGroupCodeNameIsValid] = useState(initialState.specialtyGroupCodeNameIsValid);
  const [taxonomyCode, setTaxonomyCode] = useState(initialState.taxonomyCode);
  const [taxonomyCrosswalks, setTaxonomyCrosswalks] = useState(initialState.taxonomyCrosswalks);
  const [taxonomySpecialtyCode, setTaxonomySpecialtyCode] = useState(initialState.taxonomySpecialtyCode);
  const [taxonomySpecialtyCodeLineItems, setTaxonomySpecialtyCodeLineItems] = useState(initialState.taxonomySpecialtyCodeLineItems);
  const [telehealthCredit, setTelehealthCredit] = useState(initialState.telehealthCredit);
  const [usingRatios, setUsingRatios] = useState(initialState.usingRatios);

  const [countDistinct, setCountDistinct] = useState(initialState.countDistinct);
  const [minimumServicingCounts, setMinimumServicingCounts] = useState(initialState.minimumServicingCounts);
  const [providerCount, setProviderCount] = useState(initialState.providerCount);
  const [servicingDetailsData, setServicingDetailsData] = useState(initialState.servicingDetailsData);
  const [specialtyAccessCriteria, setSpecialtyAccessCriteria] = useState(initialState.specialtyAccessCriteria);
  const [accessCustomizationsData, setAccessCustomizationData] = useState(initialState.accessCustomizationsData);
  const [accessCustomizations, setAccessCustomizations] = useState(initialState.accessCustomizations);
  const [counties, setCounties] = useState(initialState.counties);
  const [memberCounts, setMemberCounts] = useState(initialState.memberCounts);
  const [panelIsValid, setPanelIsValid] = useState(initialState.panelIsValid);
  const [servicingDetailsErrorMessages, setServicingDetailsErrorMessages] = useState(initialState.servicingDetailsErrorMessages);
  const [highlightErrors, setHighlightErrors] = useState(initialState.highlightErrors);
  const [duplicateSpecialtyCodeLineItem, setDuplicateSpecialtyCodeLineItem] = useState(initialState.duplicateSpecialtyCodeLineItem);

  /* oxlint-enable sort-keys */

  useEffect(() => {
    setLoading(true);
    const onMount = async () => {
      const specialtyGroupsData: SpecialtyGroupModel[] = props.templateDetails?.categories?.flatMap(sg => sg.specialtyGroups);
      const servicingDetailsGeographyIds =
        [...new Set(
          [...specialtyGroupsData[0]?.accessCriteria?.map(x => x.geographyId),
          ...specialtyGroupsData[0]?.servicingCriteria?.map(x => x.geographyId)]
        )];

      const codeMapSetService = new CodeMapSetService();
      const userCodeMaps = await codeMapSetService.get(CodeMapTypeEnum.SpecialtyCodeMap);
      setTaxonomyCrosswalks(userCodeMaps?.map(cm => {
        return (<option key={cm.codeMapSetId} value={cm.resource.resourceId}>{cm.resource.name}</option>)
      }));

      const accessCodeMaps = (await codeMapSetService.getCodeMapItems(props.countyClassificationMap))
        .map(cmi => cmi.codeMapValue)
        .filter((v, i, s) => s.indexOf(v) === i);

      const newAccessCustomizations = props.customizationDetails ? props.customizationDetails : accessCustomizations;
      const newSpecialtyAccessCriteria = props.servicingDetails ? props.servicingDetails : await getDefaultAccessTableData(accessCodeMaps);

      setAccessCustomizations(newAccessCustomizations);
      setCounties(servicingDetailsGeographyIds.filter((x) => !accessCodeMaps.includes(x)));
      setProviderCount(props.providerCount ? props.providerCount : 0);
      setSpecialtyAccessCriteria(newSpecialtyAccessCriteria);

      createServicingDetailsWafflesTable(newAccessCustomizations);
      createCustomizationsWafflesTable(newSpecialtyAccessCriteria);

      setLoading(false);
    }

    onMount();
  }, []);

  useEffect(() => {
    return () => {
      window.clearInterval(nameValidationIntervalRef.current);
    }
  }, []);

  const handleCategoryChange = async (e: string) => {
    setCategoryDisplayName(e);
  }

  const getDefaultAccessTableData = async (defaultAccessCriteria: string[]): Promise<ServicingDetailsModel[]> => {
    const accessCustomizationTableData = defaultAccessCriteria.map((x) => {
      // oxlint-disable-next-line sort-keys
      const sd: ServicingDetailsModel = {
        calculationMethod: CalculationMethodEnum.Unknown,
        geographyId: x,
        CountyIsValid: true,
        MaximumDistanceAllowedIsValid: false,
        MaximumTimeAllowedIsValid: false,
        MemberCountIsValid: false,
        MinimumProviderCountIsValid: false,
        PassPercentageIsValid: false,
        ServicingMinimumCountIsValid: true,
        maximumDistanceAllowed: 0,
        maximumTimeAllowed: 0,
        memberCounts: 0,
        minimumProviderCount: 0,
        passPercentage: 0,
        servicingMinimumCount: 0,
        servicingRatio: "0:0",
      };
      return sd;
    });
    return accessCustomizationTableData;
  }

  const handleAddAccessCustomization = async () => {
    const newAccessCustomizations = accessCustomizations;
    // oxlint-disable-next-line sort-keys
    const emptyAccessCustomization: ServicingDetailsModel = {
      calculationMethod: CalculationMethodEnum.Unknown,
      geographyId: '',
      CountyIsValid: true,
      MaximumDistanceAllowedIsValid: false,
      MaximumTimeAllowedIsValid: false,
      MemberCountIsValid: false,
      MinimumProviderCountIsValid: false,
      PassPercentageIsValid: false,
      ServicingMinimumCountIsValid: true,
      maximumDistanceAllowed: 0,
      maximumTimeAllowed: 0,
      memberCounts: 0,
      minimumProviderCount: 0,
      passPercentage: 0,
      servicingMinimumCount: 0,
      servicingRatio: "0:0",
    }
    newAccessCustomizations.push(emptyAccessCustomization);
    setAccessCustomizations(newAccessCustomizations);
    createCustomizationsWafflesTable(newAccessCustomizations);
  }

  const handleTaxonomyCrosswalkChange = async (e: string) => {
    setSelectedTaxonomyCrosswalk(e);
  }

  const handleMinimumProviderCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value[1];
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2))) {
      const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).minimumProviderCount = parseInt(value, 10);
      if (customization) {
        setAccessCustomizations(newAccessCriteriaValues);
        createCustomizationsWafflesTable(newAccessCriteriaValues);
      } else {
        setSpecialtyAccessCriteria(newAccessCriteriaValues);
        createServicingDetailsWafflesTable(newAccessCriteriaValues);
      }
      handleValidateMinimumProviderCount(value, geographyId, customization);

    } else {
      handleValidateMinimumProviderCount(value, geographyId, customization);
      e.preventDefault();
    }
  }

  const handleCountyChange = async (e: ChangeEvent<HTMLSelectElement>, priorGeoId: string) => {
    const newAccessCriteriaValues = accessCustomizations;

    newAccessCriteriaValues.find(x => x.geographyId === priorGeoId).geographyId = e.target.value;
    setAccessCustomizations(newAccessCriteriaValues);
    createCustomizationsWafflesTable(newAccessCriteriaValues);
  }

  const handleMaximumDistanceAllowedChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow4Digits))) {
      const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).maximumDistanceAllowed = parseInt(value, 10);
      if (customization) {
        setAccessCustomizations(newAccessCriteriaValues);
      } else {
        setSpecialtyAccessCriteria(newAccessCriteriaValues);
      }

      handleValidateMaximumDistanceAllowed(value, geographyId, customization);

    } else {
      handleValidateMaximumDistanceAllowed(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  const handleMaximumTimeAllowedChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow4Digits))) {
      const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).maximumTimeAllowed = parseInt(value, 10);
      if (customization)
        setAccessCustomizations(newAccessCriteriaValues);
      else
        setSpecialtyAccessCriteria(newAccessCriteriaValues);

      handleValidateMaximumTimeAlowed(value, geographyId, customization);

    } else {
      handleValidateMaximumTimeAlowed(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  const handlePassPercentageChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    } else {
      value = String(parseInt(value));
    }
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
      const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
      newAccessCriteriaValues.find(x => x.geographyId === geographyId).passPercentage = parseInt(value, 10);
      if (customization)
        setAccessCustomizations(newAccessCriteriaValues);
      else
        setSpecialtyAccessCriteria(newAccessCriteriaValues);

      handleValidatePassPercentage(value, geographyId, customization);

    } else {
      handleValidatePassPercentage(value, geographyId, customization, false);
      e.preventDefault();
    }
  }

  const handleServicingMinimumCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value;
    if (isNaN(parseFloat(value))) { //default the value to Zero if it is an invalid number
      value = "0";
    } else {
      value = String(parseFloat(value));
    }
    if (usingRatios === false) {
      if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsNoLimit))) {
        const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingMinimumCount = parseInt(value, 10);
        if (customization)
          setAccessCustomizations(newAccessCriteriaValues);
        else
          setSpecialtyAccessCriteria(newAccessCriteriaValues);

        handleValidateMinimumServicingProviderCount(value, geographyId, customization);

      } else {
        handleValidateMinimumServicingProviderCount(value, geographyId, customization, false);
        e.preventDefault();
      }
    } else {
      if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod))) {
        const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
        const currentRow = newAccessCriteriaValues.find(x => x.geographyId === geographyId);
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingMinimumCount = value;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingRatio = usingRatios ? value + ":" + currentRow.memberCounts : "0:0";
        if (customization)
          setAccessCustomizations(newAccessCriteriaValues);
        else
          setSpecialtyAccessCriteria(newAccessCriteriaValues);

        handleValidateMinimumServicingProviderCount(value, geographyId, customization);
      } else {
        handleValidateMinimumServicingProviderCount(value, geographyId, customization, false);
        e.preventDefault();
      }
    }
  }

  const handleCreditPercentageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    let value: string = e.target.value;
    if (isNaN(parseInt(value, 10))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
      setCreditPercentage(parseInt(value, 10));

      handleValidateCreditPercentage(value);

    } else {
      handleValidateCreditPercentage(value, false);
      e.preventDefault();
    }
  }

  const handleMembersCountChange = async (e: ChangeEvent<HTMLInputElement>, geographyId: string, customization: boolean) => {
    let value: string = e.target.value;
    if (isNaN(parseFloat(value))) { //default the value to Zero if it is an invalid number
      value = "0";
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    const currentRow = newAccessCriteriaValues.find(x => x.geographyId === geographyId);
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).memberCounts = value;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).servicingRatio = currentRow.servicingMinimumCount + ":" + value;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      handleValidateMemberCount(value, geographyId, customization, undefined, newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      handleValidateMemberCount(value, geographyId, customization, undefined, undefined, newAccessCriteriaValues);
    }
  }

  const handleCalculationMethodChange = async (e: CalculationMethodEnum, geographyId: string, customization: boolean) => {
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).calculationMethod = e;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleRemoveAccessCustomization = async (geographyId: string) => {
    const filteredAccessCustomizations = accessCustomizations.filter(x => x.geographyId !== geographyId);
    setAccessCustomizations(filteredAccessCustomizations);
    createCustomizationsWafflesTable(filteredAccessCustomizations);
  }

  const handleSpecialtyGroupCodeChange = async (e: string) => {
    const specialtyGroupCodes: string[] = props.templateDetails.categories.flatMap(cat => cat.specialtyGroups).flatMap(sg => sg.code);
    setSpecialtyGroupCode(e);
    setSpecialtyGroupCodeIsValid(!specialtyGroupCodes.some(code => code.toLowerCase() === e.toLowerCase()) && e.length > 0);
    validateName();
  }

  const handleSpecialtyGroupNameChange = async (e: string) => {
    const specialtyGroupNames: string[] = props.templateDetails.categories.flatMap(cat => cat.specialtyGroups).flatMap(sg => sg.name);
    setSpecialtyGroupCodeName(e);
    setSpecialtyGroupCodeNameIsValid(!specialtyGroupNames.some(name => name?.toLowerCase() === e.toLowerCase()) && e.length > 0);
    validateName();
  }

  const handleValidateMinimumProviderCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) == 1 || parseInt(value, 10) == 2;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MinimumProviderCountIsValid = isvalid;
    if (customization)
      setAccessCustomizations(newAccessCriteriaValues);
    else
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
  }

  const handleValidateMaximumDistanceAllowed = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) >= 1 && parseInt(value, 10) < 10000;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MaximumDistanceAllowedIsValid = isvalid;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleValidateMaximumTimeAlowed = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) >= 1 && parseInt(value, 10) < 10000;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MaximumTimeAllowedIsValid = isvalid;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleValidatePassPercentage = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = (accessReqInCounty || skipAccessTimeAndDistanceCalc) || parseInt(value, 10) <= 100 && parseInt(value, 10) > 0;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).PassPercentageIsValid = isvalid;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleValidateMinimumServicingProviderCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean) => {
    let isvalid = !isNaN(parseFloat(value));
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    const newAccessCriteriaValues = customization ? accessCustomizations : specialtyAccessCriteria;
    if (usingRatios) {
      if (customization) {
        isvalid = +value <= +accessCustomizations.find(x => x.geographyId === geographyId).memberCounts;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;
      } else {
        isvalid = +value <= +specialtyAccessCriteria.find(x => x.geographyId === geographyId).memberCounts;
        newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;
      }
    }
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).ServicingMinimumCountIsValid = isvalid;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isvalid;

    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleValidateMemberCount = async (value: string, geographyId: string, customization: boolean, setIsValidTo?: boolean, newAccessCustomizations: ServicingDetailsModel[] = accessCustomizations, newSpecialtyAccessCriteria: ServicingDetailsModel[] = specialtyAccessCriteria) => {
    let isValid = +value >= (customization
      ? +newAccessCustomizations.find(x => x.geographyId === geographyId).servicingMinimumCount
      : +newSpecialtyAccessCriteria.find(x => x.geographyId === geographyId).servicingMinimumCount);

    if (typeof setIsValidTo != 'undefined') {
      isValid = setIsValidTo;
    }

    const newAccessCriteriaValues = customization ? newAccessCustomizations : newSpecialtyAccessCriteria;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).MemberCountIsValid = isValid;
    newAccessCriteriaValues.find(x => x.geographyId === geographyId).ServicingMinimumCountIsValid = isValid;
    if (customization) {
      setAccessCustomizations(newAccessCriteriaValues);
      createCustomizationsWafflesTable(newAccessCriteriaValues);
    } else {
      setSpecialtyAccessCriteria(newAccessCriteriaValues);
      createServicingDetailsWafflesTable(newAccessCriteriaValues);
    }
  }

  const handleValidateSpecialtyCodeProviderWeight = async (value: string, setIsValidTo?: boolean) => {

    if (!isNaN(parseFloat(value))) {
      let isvalid = parseFloat(value) <= 1;
      if (typeof setIsValidTo != 'undefined') {
        isvalid = setIsValidTo;
      }
      let specCodeProvWeightIsValid = specialtyCodeProviderWeightIsValid;
      specCodeProvWeightIsValid = isvalid;
      setSpecialtyCodeProviderWeightIsValid(specCodeProvWeightIsValid);
    }
  }

  const handleSpecialtyCodeProviderWeightChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (execRegex(value, new RegExp(InputValidationRegExps.regexpOnlyAllowDigitsLessThanEqual1AndAllowDecimalPlace))) {
      setSpecialtyCodeProviderWeight(value);

      handleValidateSpecialtyCodeProviderWeight(value);

    } else {
      handleValidateSpecialtyCodeProviderWeight(value, false);
      e.preventDefault();
    }
  }

  const handleValidateCreditPercentage = async (value: string, setIsValidTo?: boolean) => {
    let isvalid = parseInt(value, 10) <= 100;
    if (typeof setIsValidTo != 'undefined') {
      isvalid = setIsValidTo;
    }
    let specCodeProvWeightIsValid = creditPercentageIsValid;
    specCodeProvWeightIsValid = isvalid;
    setCreditPercentageIsValid(specCodeProvWeightIsValid);
  }

  const execRegex = (value: string, regexToExecute: RegExp, allowEmptyString: boolean = true): boolean => {
    if (allowEmptyString === true && value.length === 0) {
      return true;
    }
    const ret = regexToExecute.exec(value);
    if (ret) {
      return ret.length > 0;
    } else {
      return false;
    }
  }

  const validateName = async () => {
    window.clearInterval(nameValidationIntervalRef.current);
  }

  const handleAddClick = () => {
    validatePanel();
    if (!panelIsValid) {
      setHighlightErrors(true);
      createServicingDetailsWafflesTable(undefined, true);
      createCustomizationsWafflesTable(undefined, true);
      return;
    }

    const totalCriteria = specialtyAccessCriteria.concat(accessCustomizations);
    setSpecialtyAccessCriteria(totalCriteria);

    const accessCriteria: AccessCriteriaModel[] = [];
    const servicingCriteria: ServicingCriteriaModel[] = [];
    totalCriteria.forEach(newCriteria => {
      accessCriteria.push({
        calculationMethod: newCriteria.calculationMethod,
        cmsApplicationCreditPercentage: 0,
        conAccessCreditPercentage: 0,
        geographyId: newCriteria.geographyId,
        isAccessCustomization: counties.includes(newCriteria.geographyId),
        isTelehealthAccessCreditCustomization: telehealthCredit,
        isValid: true,
        maximumDistanceAllowed: newCriteria.maximumDistanceAllowed,
        maximumTimeAllowed: newCriteria.maximumTimeAllowed,
        minimumProviderCount: newCriteria.minimumProviderCount,
        passPercentage: newCriteria.passPercentage,
        providerRegionType: accessReqInCounty ? ProviderRegionTypeEnum.InRegion : ProviderRegionTypeEnum.InRegionOrCoversMember,
        servicingCountMethodology: "",
        skipAccessTimeAndDistanceCalculations: skipAccessTimeAndDistanceCalc,
        telehealthAccessCreditPercentage: creditPercentage,
      });

      servicingCriteria.push({
        geographyId: newCriteria.geographyId,
        servicingAnalysisType: servicingReqInCounty ? ServicingAnalysisTypeEnum.InRegion : ServicingAnalysisTypeEnum.InRegionOrCoversMember,
        servicingMinimumCount: Number(newCriteria.servicingMinimumCount),
        servicingRatio: newCriteria.servicingRatio,
      });
    })

    props.handleSave(
      accessReqInCounty,
      categoryDisplayName,
      creditPercentage,
      expandable,
      isProvider,
      loading,
      informational,
      selectedTaxonomyCrosswalk,
      servicingReqInCounty,
      accessCriteria,
      servicingCriteria,
      specialtyCode.trim(),
      specialtyCodeLineItems,
      specialtyCodeName.trim(),
      specialtyCodeProviderWeight,
      specialtyGroupCode.trim(),
      specialtyGroupCodeName.trim(),
      taxonomyCode.trim(),
      taxonomyCrosswalks,
      taxonomySpecialtyCode.trim(),
      taxonomySpecialtyCodeLineItems,
      telehealthCredit,
      usingRatios,
      countDistinct,
      Number(minimumServicingCounts),
      providerCount,
      props.index,
      accessCustomizations,
      specialtyAccessCriteria,
    );
    flyoutContext.handleCloseFlyout();
  }

  const validatePanel = () => {
    const newServicingDetailsErrorMessages = [];

    if (specialtyAccessCriteria.find(x => !x.MinimumProviderCountIsValid) ||
      accessCustomizations.find(x => !x.MinimumProviderCountIsValid)) {
      newServicingDetailsErrorMessages.push("Required Providers must be 1 or 2");
    }

    if (specialtyAccessCriteria.find(x => !x.MaximumDistanceAllowedIsValid && skipAccessTimeAndDistanceCalc === false) ||
      accessCustomizations.find(x => !x.MaximumDistanceAllowedIsValid && skipAccessTimeAndDistanceCalc === false)) {
      newServicingDetailsErrorMessages.push("Miles cannot be greater than 9,999 or less than 1");
    }

    if (specialtyAccessCriteria.find(x => !x.MaximumTimeAllowedIsValid && skipAccessTimeAndDistanceCalc === false) ||
      accessCustomizations.find(x => !x.MaximumTimeAllowedIsValid && skipAccessTimeAndDistanceCalc === false)) {
      newServicingDetailsErrorMessages.push("Minutes cannot be greater than 9,999 or less than 1");
    }

    if (specialtyAccessCriteria.find(x => !x.PassPercentageIsValid) ||
      accessCustomizations.find(x => !x.PassPercentageIsValid)) {
      newServicingDetailsErrorMessages.push("Pass % must be greater than 0 and less than 100");
    }

    if (usingRatios === false && (
      specialtyAccessCriteria.find(x => !x.ServicingMinimumCountIsValid) ||
      accessCustomizations.find(x => !x.ServicingMinimumCountIsValid))) {
      newServicingDetailsErrorMessages.push("Servicing Providers must be a number");
    }

    if (usingRatios && (
      specialtyAccessCriteria.find(x => !x.ServicingMinimumCountIsValid) ||
      accessCustomizations.find(x => !x.ServicingMinimumCountIsValid))) {
      newServicingDetailsErrorMessages.push("Servicing Providers must be less than Member Count");
    }

    if (specialtyAccessCriteria.find(x => !x.MemberCountIsValid) ||
      accessCustomizations.find(x => !x.MemberCountIsValid)) {
      newServicingDetailsErrorMessages.push("Member Count must be greater than Servicing Minimum Count");
    }

    if (!specialtyCodeProviderWeightIsValid || !specialtyGroupCodeIsValid || !specialtyGroupCodeNameIsValid
      || specialtyCodeLineItems.length === 0 || specialtyGroupCode.length === 0
      || specialtyCodeLineItems.find(x => x.specialtyCode.length === 0 || x.specialtyCodeName.length === 0)
      || countDistinct === "unknown" || newServicingDetailsErrorMessages.length > 0) {
      setPanelIsValid(false);
      setServicingDetailsErrorMessages(newServicingDetailsErrorMessages);
    } else {
      setPanelIsValid(true);
      setServicingDetailsErrorMessages(newServicingDetailsErrorMessages);
    }
  }

  const createServicingDetailsWafflesTable = async (updatedData: ServicingDetailsModel[] = specialtyAccessCriteria, newHighlightErrors: boolean = highlightErrors): Promise<ServicingDetailsRow[]> => {
    const rowData = updatedData.map(rec => {
      /* oxlint-disable sort-keys */
      return ({
        key: rec.geographyId,
        geographyId: <>{rec.geographyId}</>,
        requiredProviders:
          (<QESTextInput
            key={rec.geographyId}
            label="Required Providers"
            testId="edit-specialtyGroupServicingDetails__requiredProviders"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2, false, true); }}
            onChange={(e) => { handleMinimumProviderCountChange(e, rec.geographyId, false) }}
            value={rec.minimumProviderCount}
            isValid={rec.MinimumProviderCountIsValid}
            className={!rec.MinimumProviderCountIsValid && newHighlightErrors ? "border border-danger" : ""}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        miles:
          (<QESTextInput
            label="Miles"
            testId="edit-specialtyGroupServicingDetails__miles"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaximumDistanceAllowedChange(e, rec.geographyId, false); }}
            isValid={rec.MaximumDistanceAllowedIsValid}
            className={!rec.MaximumDistanceAllowedIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.maximumDistanceAllowed}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        minutes:
          (<QESTextInput
            label="Minutes"
            testId="edit-specialtyGroupServicingDetails__minutes"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaximumTimeAllowedChange(e, rec.geographyId, false) }}
            isValid={rec.MaximumTimeAllowedIsValid}
            className={!rec.MaximumTimeAllowedIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.maximumTimeAllowed}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        passPercentage:
          (<QESTextInput
            label="Pass %"
            testId="edit-specialtyGroupServicingDetails__passPercentage"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100, false, e.currentTarget.value === "0"); }}
            onChange={(e) => { handlePassPercentageChange(e, rec.geographyId, false) }}
            isValid={rec.PassPercentageIsValid}
            className={!rec.PassPercentageIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.passPercentage}
            hideLabel={true}
            unrequiredValidation={true}
          />),
        servicingProviders:
          <div className="flex-container">
            {usingRatios === false && (
              <QESTextInput
                label="Servicing Providers"
                testId="edit-specialtyGroupServicingDetails__servicingProviders"
                containerClassName={usingRatios ? "d-inline-block w-50 pe-1" : ""}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit, false, e.currentTarget.value === "0"); }}
                onChange={(e) => { handleServicingMinimumCountChange(e, rec.geographyId, false) }}
                isValid={specialtyAccessCriteria.find(x => x.geographyId === rec.geographyId)?.ServicingMinimumCountIsValid}
                className={!rec.ServicingMinimumCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                value={rec.servicingMinimumCount}
                hideLabel={true}
                unrequiredValidation={true}
              />
            )}
            {usingRatios === true && (
              <>
                <QESTextInput
                  label="Servicing Providers"
                  testId="edit-specialtyGroupServicingDetails__servicingProviders"
                  containerClassName={usingRatios ? "d-inline-block w-50 pe-1" : ""}
                  className={!rec.ServicingMinimumCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                  isValid={rec.ServicingMinimumCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true, e.currentTarget.value === "0"); }}
                  onChange={(e) => { handleServicingMinimumCountChange(e, rec.geographyId, false) }}
                  value={rec.servicingMinimumCount}
                  hideLabel={true}
                  unrequiredValidation={true}
                />
                <QESTextInput
                  label="Members"
                  testId="edit-specialtyGroupServicingDetails__members"
                  containerClassName="d-inline-block w-50 ps-1"
                  className={!rec.MemberCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                  isValid={rec.MemberCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true, e.currentTarget.value === "0"); }}
                  onChange={(e) => { handleMembersCountChange(e, rec.geographyId, false); }}
                  value={rec.memberCounts}
                  hideLabel={true}
                  unrequiredValidation={true}
                />
              </>
            )}
          </div>,
        calculationMethod:
          (<QESSelect
            testId="create-manage-specialtyGroup-panel-content__urbanCalculationMethod"
            containerClassName="mb-1 ms-4"
            label=""
            onChange={(e) => {
              handleCalculationMethodChange(e.target.value as CalculationMethodEnum, rec.geographyId, false);
            }}
            defaultValue={rec.calculationMethod}
          >
            {calculationMethodDropdownValues.map((calculationMethod, index) => <option key={index} value={calculationMethod.value}>{calculationMethod.displayText}</option>)}
          </QESSelect>),
      });
    });

    validatePanel();
    setServicingDetailsData(rowData);
    return rowData;
  }

  const createCustomizationsWafflesTable = async (updatedData: ServicingDetailsModel[] = accessCustomizations, newHighlightErrors: boolean = highlightErrors): Promise<ServicingDetailsRow[]> => {
    const rowData = updatedData.map(rec => {
      /* oxlint-disable sort-keys */
      return ({
        key: rec.geographyId,
        geographyId: (<QESSelect
          testId="edit-accessCustomizations__geographyId"
          containerClassName="mb-1 ms-4"
          label=""
          value={rec.geographyId}
          isValid={rec.CountyIsValid}
          onChange={(e) => {
            handleCountyChange(e, rec.geographyId);
          }}
          defaultValue={rec.geographyId}
        >
          {counties.map((county, index) => <option key={index} value={county}>{county}</option>)}
        </QESSelect>),
        requiredProviders:
          (<QESTextInput
            key={rec.geographyId}
            label="Required Providers"
            testId="edit-accessCustomizations__requiredProviders"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2, false, true); }}
            onChange={(e) => { handleMinimumProviderCountChange(e, rec.geographyId, true) }}
            value={rec.minimumProviderCount}
            isValid={rec.MinimumProviderCountIsValid}
            className={!rec.MinimumProviderCountIsValid && newHighlightErrors ? "border border-danger" : ""}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        miles:
          (<QESTextInput
            label="Miles"
            testId="edit-accessCustomizations__miles"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaximumDistanceAllowedChange(e, rec.geographyId, true); }}
            isValid={rec.MaximumDistanceAllowedIsValid}
            className={!rec.MaximumDistanceAllowedIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.maximumDistanceAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        minutes:
          (<QESTextInput
            label="Minutes"
            testId="edit-accessCustomizations__minutes"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaximumTimeAllowedChange(e, rec.geographyId, true) }}
            isValid={rec.MaximumTimeAllowedIsValid}
            className={!rec.MaximumTimeAllowedIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.maximumTimeAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        passPercentage:
          (<QESTextInput
            label="Pass %"
            testId="edit-accessCustomizations__passPercentage"
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100, false, e.currentTarget.value === "0"); }}
            onChange={(e) => { handlePassPercentageChange(e, rec.geographyId, true) }}
            isValid={rec.PassPercentageIsValid}
            className={!rec.PassPercentageIsValid && newHighlightErrors ? "border border-danger" : ""}
            value={rec.passPercentage}
            hideLabel={true}
            unrequiredValidation={true}
            disabled={!rec.geographyId}
          />),
        servicingProviders:
          <div className="flex-container">
            {usingRatios === false && (
              <QESTextInput
                label="Servicing Providers"
                testId="edit-accessCustomizations__servicingProviders"
                containerClassName={usingRatios ? "d-inline-block w-50 pe-1" : ""}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit, false, e.currentTarget.value === "0"); }}
                onChange={(e) => { handleServicingMinimumCountChange(e, rec.geographyId, true) }}
                isValid={specialtyAccessCriteria.find(x => x.geographyId === rec.geographyId)?.ServicingMinimumCountIsValid ?? true}
                className={!rec.ServicingMinimumCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                value={rec.servicingMinimumCount}
                hideLabel={true}
                unrequiredValidation={true}
                disabled={!rec.geographyId}
              />
            )}
            {usingRatios === true && (
              <>
                <QESTextInput
                  label="Servicing Providers"
                  testId="edit-accessCustomizations__servicingProviders"
                  containerClassName={usingRatios ? "d-inline-block w-50 pe-1" : ""}
                  className={!rec.ServicingMinimumCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                  isValid={rec.ServicingMinimumCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true, e.currentTarget.value === "0"); }}
                  onChange={(e) => { handleServicingMinimumCountChange(e, rec.geographyId, true) }}
                  value={rec.servicingMinimumCount}
                  hideLabel={true}
                  unrequiredValidation={true}
                  disabled={!rec.geographyId}
                />
                <QESTextInput
                  label="Members"
                  testId="edit-accessCustomizations__members"
                  containerClassName="d-inline-block w-50 ps-1"
                  className={!rec.MemberCountIsValid && newHighlightErrors ? "border border-danger" : ""}
                  isValid={rec.MemberCountIsValid}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true, e.currentTarget.value === "0"); }}
                  onChange={(e) => { handleMembersCountChange(e, rec.geographyId, true); }}
                  value={rec.memberCounts}
                  hideLabel={true}
                  unrequiredValidation={true}
                  disabled={!rec.geographyId}
                />
              </>
            )}
          </div>,
        calculationMethod:
          <QESSelect
            testId="edit-accessCustomizations__urbanCalculationMethod"
            containerClassName="mb-1 ms-4"
            label=""
            onChange={(e) => {
              handleCalculationMethodChange(e.target.value as CalculationMethodEnum, rec.geographyId, true);
            }}
            defaultValue={rec.calculationMethod}
            disabled={!rec.geographyId}
            value={rec.calculationMethod}
          >
            {calculationMethodDropdownValues.map((calculationMethod, index) => <option key={index} value={calculationMethod.value}>{calculationMethod.displayText}</option>)}
          </QESSelect>,
        remove:
          <QESButton
            qesTestId="edit-accessCustomizations__remove"
            className="mt10"
            qesType="secondary-danger"
            onClick={() => handleRemoveAccessCustomization(rec.geographyId)}>
            Remove
          </QESButton>
      });
    });

    validatePanel();
    setAccessCustomizationData(rowData);
    return rowData;
  }

  const classificationColumnConfig: ColumnConfig = {
    geographyId: {
      displayText: "Standard",
      sizeContentToFit: true,
      testId: "specialtyGroup-geographyId"
    },
    requiredProviders: {
      displayText: "Required Providers",
      sizeContentToFit: true,
      testId: "specialtyGroup-requiredProviders",
      tooltip: "Access Providers must be 1 or 2",
      displayElement: <>Required Providers <span className="text-danger">*</span></>
    },
    miles: {
      displayText: "Miles",
      sizeContentToFit: true,
      testId: "specialtyGroup-miles",
      tooltip: "Miles cannot be greater than 9,999",
    },
    minutes: {
      displayText: "Minutes",
      sizeContentToFit: true,
      testId: "specialtyGroup-minutes",
      tooltip: "Minutes cannot be greater than 9,999",
    },
    passPercentage: {
      displayText: "Pass %",
      sizeContentToFit: true,
      testId: "specialtyGroup-passPercentage",
      tooltip: "Pass percentage cannot be greater than 100",
      displayElement: <>Pass % {(!accessReqInCounty && !skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "specialtyGroup-servicingProviders",
      tooltip: usingRatios ? "These fields should be populated as Provider to Member Ratios. Ex. 1:1000" : "Access Providers cannot be greater than Servicing Providers",
    },
    calculationMethod: {
      displayText: "Calculation Method",
      sizeContentToFit: true,
      testId: "specialtyGroup-calculationMethod",
    }
  };

  const classificationSkipAccessCheckedColumnConfig: ColumnConfig = {
    geographyId: {
      displayText: "Standard",
      sizeContentToFit: true,
      testId: "specialtyGroup-geographyId"
    },
    requiredProviders: {
      displayText: "Required Providers",
      sizeContentToFit: true,
      testId: "specialtyGroup-requiredProviders",
      displayElement: <>Required Providers <span className="text-danger">*</span></>
    },
    passPercentage: {
      displayText: "Pass %",
      sizeContentToFit: true,
      testId: "specialtyGroup-passPercentage",
      displayElement: <>Pass % {(!accessReqInCounty && !skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "specialtyGroup-servicingProviders",
    },
    calculationMethod: {
      displayText: "Calculation Method",
      sizeContentToFit: true,
      testId: "specialtyGroup-calculationMethod",
    }
  };

  const customizationColumnConfig: ColumnConfig = {
    geographyId: {
      displayText: "County SSA",
      sizeContentToFit: true,
      testId: "accessCustomization-geographyId"
    },
    requiredProviders: {
      displayText: "Required Providers",
      sizeContentToFit: true,
      testId: "accessCustomization-requiredProviders",
      tooltip: "Access Providers must be 1 or 2",
      displayElement: <>Required Providers <span className="text-danger">*</span></>
    },
    miles: {
      displayText: "Miles",
      sizeContentToFit: true,
      testId: "accessCustomization-miles",
      tooltip: "Miles cannot be greater than 9,999",
    },
    minutes: {
      displayText: "Minutes",
      sizeContentToFit: true,
      testId: "accessCustomization-minutes",
      tooltip: "Minutes cannot be greater than 9,999",
    },
    passPercentage: {
      displayText: "Pass %",
      sizeContentToFit: true,
      testId: "accessCustomization-passPercentage",
      tooltip: "Pass percentage cannot be greater than 100",
      displayElement: <>Pass % {(!accessReqInCounty && !skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "accessCustomization-servicingProviders",
      tooltip: usingRatios ? "These fields should be populated as Provider to Member Ratios. Ex. 1:1000" : "Access Providers cannot be greater than Servicing Providers",
    },
    calculationMethod: {
      displayText: "Calculation Method",
      sizeContentToFit: true,
      testId: "accessCustomization-calculationMethod",
    },
    remove: {
      displayText: "",
      sizeContentToFit: true,
      testId: "accessCustomiztion-remove"
    }
  };

  const customizationSkipAccessCheckedColumnConfig: ColumnConfig = {
    geographyId: {
      displayText: "County SSA",
      sizeContentToFit: true,
      testId: "accessCustomization-geographyId"
    },
    requiredProviders: {
      displayText: "Required Providers",
      sizeContentToFit: true,
      testId: "accessCustomization-requiredProviders",
      displayElement: <>Required Providers <span className="text-danger">*</span></>
    },
    passPercentage: {
      displayText: "Pass %",
      sizeContentToFit: true,
      testId: "accessCustomization-passPercentage",
      displayElement: <>Pass % {(!accessReqInCounty && !skipAccessTimeAndDistanceCalc) && <span className="text-danger">*</span>}</>
    },
    servicingProviders: {
      displayText: "Servicing Providers",
      sizeContentToFit: true,
      testId: "accessCustomization-servicingProviders",
    },
    calculationMethod: {
      displayText: "Calculation Method",
      sizeContentToFit: true,
      testId: "accessCustomization-calculationMethod",
    },
    remove: {
      displayText: "",
      sizeContentToFit: true,
      testId: "access-Customization-remove"
    }
  };

  const namedCategories = props.templateDetails?.categories?.filter(function (category) {
    return category.displayName != "" && category.displayName != undefined && category.displayName != null;
  });

  if (namedCategories?.length == 1) {
    setCategoryDisplayName(namedCategories[0].displayName);
  }

  const updateCreditPercentage = (telehealthCredit: boolean) => {
    telehealthCredit ? setCreditPercentage(10) : setCreditPercentage(0);
  }

  const handleAdd = () => {
    const newSpecialtyCodeLineItems: SpecialtyCodeLineItem[] = cloneDeep(specialtyCodeLineItems);

    const newItem = {
      id: newSpecialtyCodeLineItems.length,
      specialtyCode: specialtyCode.trim(),
      specialtyCodeName: specialtyCodeName.trim(),
      specialtyCodeProviderWeight: specialtyCodeProviderWeight,
    } as SpecialtyCodeLineItem;

    let isDuplicate = false;

    newSpecialtyCodeLineItems.forEach(lineItem => {
      if (newItem.specialtyCode === lineItem.specialtyCode
        || newItem.specialtyCodeName === lineItem.specialtyCodeName) {
        isDuplicate = true;
      }
    });
    if (!isDuplicate) {
      setDuplicateSpecialtyCodeLineItem(false);
      newSpecialtyCodeLineItems.push(newItem);
      setSpecialtyCode("");
      setSpecialtyCodeName("");
      setSpecialtyCodeProviderWeight("1");
      setSpecialtyCodeLineItems(newSpecialtyCodeLineItems);
    }
    else {
      setDuplicateSpecialtyCodeLineItem(true);
    }
  }

  const handleRemove = (lineItem: SpecialtyCodeLineItem) => {
    let newSpecialtyCodeLineItems: SpecialtyCodeLineItem[] = cloneDeep(specialtyCodeLineItems);
    newSpecialtyCodeLineItems = newSpecialtyCodeLineItems.filter(function (item) {
      return item.id !== lineItem.id;
    });
    setSpecialtyCodeLineItems(newSpecialtyCodeLineItems);
    setDuplicateSpecialtyCodeLineItem(false);
  }

  const handleTaxonomySpecialtyCodeAdd = () => {
    const newTaxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[] = cloneDeep(taxonomySpecialtyCodeLineItems);

    const newItem = {
      id: newTaxonomySpecialtyCodeLineItems?.length,
      taxonomyCode: taxonomyCode,
      taxonomySpecialtyCode: taxonomySpecialtyCode,
    } as TaxonomySpecialtyCodeLineItem;
    newTaxonomySpecialtyCodeLineItems.push(newItem);
    setTaxonomyCode("");
    setTaxonomySpecialtyCode("");
    setTaxonomySpecialtyCodeLineItems(newTaxonomySpecialtyCodeLineItems);
  }

  const handleTaxonomySpecialtyCodeRemove = (lineItem: TaxonomySpecialtyCodeLineItem) => {
    let newTaxonomySpecialtyCodeLineItems: TaxonomySpecialtyCodeLineItem[] = cloneDeep(taxonomySpecialtyCodeLineItems);
    newTaxonomySpecialtyCodeLineItems = newTaxonomySpecialtyCodeLineItems.filter(function (item) {
      return item.id !== lineItem.id;
    });
    setTaxonomySpecialtyCodeLineItems(newTaxonomySpecialtyCodeLineItems);
  }

  const handleSkipAccessCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccessReqInCounty(e.target.checked);
    let updatedData = specialtyAccessCriteria;
    if (e.target.checked) {
      updatedData = specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: true }));
    }
    else {
      updatedData = specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: false }));
    }
    setSpecialtyAccessCriteria(updatedData);
    setSkipAccessTimeAndDistanceCalc(e.target.checked);
    await createServicingDetailsWafflesTable(updatedData);
    validatePanel();
  }

  const handleInCountyRequirementsCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let updatedData = specialtyAccessCriteria;
    if (e.target.checked) {
      updatedData = specialtyAccessCriteria.map(row => ({ ...row, maximumDistanceAllowed: 0, maximumTimeAllowed: 0, PassPercentageIsValid: true }));
    }
    else if (!skipAccessTimeAndDistanceCalc) {
      updatedData = specialtyAccessCriteria.map(row => ({ ...row, PassPercentageIsValid: false }));
    }
    setSpecialtyAccessCriteria(updatedData);
    setAccessReqInCounty(e.target.checked);
    createServicingDetailsWafflesTable(updatedData);
    validatePanel();
  }

  const handleProviderCountChange = async (value: string) => {
    let newValue: string = value[1];
    if (isNaN(parseInt(newValue, 10))) { //default the value to Zero if it is an invalid number
      newValue = "0";
    }
    const newProviderCountValue: number = parseInt(newValue, 10);
    let updatedData = specialtyAccessCriteria;
    let isValid = true;
    if (!execRegex(newProviderCountValue.toString(), new RegExp(InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100))) {
      isValid = false;
    }
    setProviderCount(newProviderCountValue);
    updatedData = specialtyAccessCriteria.map(row => ({ ...row, minimumProviderCount: newProviderCountValue, MinimumProviderCountIsValid: isValid }));
    setSpecialtyAccessCriteria(updatedData);
    createServicingDetailsWafflesTable(updatedData);
    createCustomizationsWafflesTable();
  }

  const handleMinimumServicingCountsChange = async (value: string) => {
    let newValue: string = value;
    if (isNaN(parseFloat(newValue))) { //default the value to Zero if it is an invalid number
      newValue = "0";
    }
    const newservicingMinimumCountsValue: number = parseFloat(newValue);
    let updatedData = specialtyAccessCriteria;
    updatedData = specialtyAccessCriteria.map(row => ({
      ...row,
      servicingMinimumCount: newservicingMinimumCountsValue,
      servicingRatio: usingRatios === true ? newservicingMinimumCountsValue + ":" + row.memberCounts : "0:0",
      ServicingMinimumCountIsValid: usingRatios ? +newservicingMinimumCountsValue <= +row.memberCounts : true,
      MemberCountIsValid: usingRatios ? +newservicingMinimumCountsValue <= +row.memberCounts : true
    }));
    setSpecialtyAccessCriteria(updatedData);
    setMinimumServicingCounts(newservicingMinimumCountsValue);
    createServicingDetailsWafflesTable(updatedData);
    createCustomizationsWafflesTable();
  }

  const handleMemberCountsChange = async (value: string) => {
    let newValue: string = value;
    if (isNaN(parseFloat(newValue))) { //default the value to Zero if it is an invalid number
      newValue = "0";
    }
    const newMemberCounts: number = parseFloat(newValue);
    let updatedData = specialtyAccessCriteria;
    updatedData = specialtyAccessCriteria.map(row => ({
      ...row,
      memberCounts: newMemberCounts,
      servicingRatio: row.servicingMinimumCount + ":" + newMemberCounts,
      ServicingMinimumCountIsValid: usingRatios ? +row.servicingMinimumCount <= +newMemberCounts : true,
      MemberCountIsValid: usingRatios ? +row.servicingMinimumCount <= +newMemberCounts : true
    }));
    setSpecialtyAccessCriteria(updatedData);
    setMemberCounts(newMemberCounts);
    createServicingDetailsWafflesTable(updatedData);
    createCustomizationsWafflesTable();
  }

  const handleUsingRatiosCheckboxToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsingRatios(e.target.checked);
    createServicingDetailsWafflesTable();
    createCustomizationsWafflesTable();
  }

  const getErrorMessages = (): JSX.Element => {
    return <>{servicingDetailsErrorMessages.map((errorMessage) => (
      <QESAlert type={"danger"} key={errorMessage} persist={true}>
        <span>{errorMessage}</span>
      </QESAlert>
    ))}
    </>
  }


  return (
    <>
      {props.loading || loading ? <LoadingSpinner /> :
        <>
          <div className="d-flex flex-row">
            <div className="w-25 p-2 bd-highlight">
              <QESTextInput
                testId={"create-manage-specialtyGroup-panel-content__specialtyGroupCode"}
                containerClassName="mb-2"
                label="Specialty Group Code"
                value={specialtyGroupCode}
                onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                onChange={(e) => {
                  handleSpecialtyGroupCodeChange(e.target.value);
                  validateName();
                }}
                isValid={specialtyGroupCodeIsValid && specialtyGroupCode.length != 0}
                required={true}
                descriptiveText={!(specialtyGroupCodeIsValid) && "A specialty group with this code already exists."}
                errorText={nameIsValidText}
                className={specialtyGroupCode.length === 0 && highlightErrors ? "border border-danger" : ""}
                wasFocusedExternally={!!props.specialtyGroup}
              />
            </div>
            <div className="w-25 p-2 bd-highlight">
              <QESTextInput
                testId={"create-manage-specialtyGroup-panel-content__specialtyGroupCodeName"}
                containerClassName="mb-2"
                label="Specialty Group Name"
                value={specialtyGroupCodeName}
                onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                onChange={(e) => handleSpecialtyGroupNameChange(e.target.value)}
                isValid={specialtyGroupCodeNameIsValid && specialtyGroupCodeName.length != 0}
                required={true}
                descriptiveText={!(specialtyGroupCodeNameIsValid) && "A specialty group with this name already exists."}
                errorText={nameIsValidText}
                className={specialtyGroupCodeName.length === 0 && highlightErrors ? "border border-danger" : ""}
                unrequiredValidation={true}
                wasFocusedExternally={!!props.specialtyGroup}
              />
            </div>
          </div>

          <div className="d-flex flex-row">
            <div className="w-25 p-2 bd-highlight">
              <QESTextInput
                testId={"create-specialty-panel-content__specialtyCodeInput"}
                containerClassName="mb-2"
                label="Specialty Code"
                value={specialtyCode}
                onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                onChange={(e) => {
                  setSpecialtyCode(e.target.value);
                  validateName();
                }}
                isValid={specialtyCode.length != 0}
                required={specialtyCodeLineItems.length === 0}
                errorText={nameIsValidText}
                className={specialtyCode.length === 0 && specialtyCodeLineItems.length === 0 && highlightErrors ? "border border-danger" : ""}
                wasFocusedExternally={!!props.specialtyGroup}
              />
            </div>
            <div className="w-25 p-2 bd-highlight">
              <QESTextInput
                testId={"create-specialty-panel-content__specialtyCodeNameInput"}
                containerClassName="mb-2"
                label="Specialty Name"
                value={specialtyCodeName}
                onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                onChange={(e) => {
                  setSpecialtyCodeName(e.target.value);
                  validateName();
                }}
                isValid={specialtyCodeName.length != 0}
                required={specialtyCodeLineItems.length === 0}
                errorText={nameIsValidText}
                className={specialtyCodeName.length === 0 && specialtyCodeLineItems.length === 0 && highlightErrors ? "border border-danger" : ""}
                wasFocusedExternally={!!props.specialtyGroup}
              />
            </div>
            <div className="p-2 bd-highlight">
              <QESTextInput
                testId={"create-specialty-panel-content__specialtyCodeProviderWeightInput"}
                containerClassName="mb-2 w-75"
                label="Provider Weight"
                value={specialtyCodeProviderWeight}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsLessThanEqual1AndAllowDecimalPlace, true); }}
                onChange={(e) => { handleSpecialtyCodeProviderWeightChange(e) }}
                isValid={specialtyCodeProviderWeightIsValid}
                required={specialtyCodeLineItems.length === 0}
                errorText={"Provider Weight cannot be greater than 1"}
                className={(!specialtyCodeProviderWeightIsValid || +specialtyCodeProviderWeight > 1) && highlightErrors ? "border border-danger" : ""}
                unrequiredValidation={true}
                wasFocusedExternally={!!props.specialtyGroup}
              />
            </div>
            <div className="pt-3 align-content-center bd-highlight">
              <QESButton
                qesTestId={`add-specialty__specialty-code__add-button`}
                qesType="secondary"
                onClick={handleAdd}
                disabled={specialtyCodeName.length === 0 || specialtyCode.length === 0}
                className={specialtyCodeLineItems.length === 0 && highlightErrors ? "border border-danger" : ""}
              >
                Add
              </QESButton>
              {duplicateSpecialtyCodeLineItem && (
                <span style={{ color: "#cc3a52", fontWeight: "bold" }}>
                  <i className="fa fa-times-circle" aria-hidden="true" /> Cannot add a specialty with a duplicate code or name.
                </span>
              )}
            </div>
          </div>

          {specialtyCodeLineItems?.length > 0 &&
            <React.Fragment>
              {
                specialtyCodeLineItems.map((lineItem) => (
                  <div className="d-flex flex-row" key={lineItem.id}>
                    <div className="w-25 p-2 bd-highlight">
                      <QESTextInput
                        testId={"create-specialty-panel-content__specialtyCodeLineItem"}
                        containerClassName="mb-2"
                        disabled={true}
                        label=""
                        value={lineItem.specialtyCode}
                        onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                        onChange={(e) => {
                          setSpecialtyCode(e.target.value);
                          validateName();
                        }}
                      />
                    </div>
                    <div className="w-25 p-2 bd-highlight">
                      <QESTextInput
                        testId={"create-specialty-panel-content__specialtyCodeNameLineItem"}
                        containerClassName="mb-2"
                        disabled={true}
                        label=""
                        value={lineItem.specialtyCodeName}
                        onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                        onChange={(e) => {
                          setSpecialtyCodeName(e.target.value);
                          validateName();
                        }}
                      />
                    </div>
                    <div className="p-2 bd-highlight">
                      <QESTextInput
                        testId={"create-specialty-panel-content__specialtyCodeProviderWeightLineItem"}
                        containerClassName="mb-2"
                        disabled={true}
                        label=""
                        value={lineItem.specialtyCodeProviderWeight}
                        onChange={(e) => {
                          setSpecialtyCodeProviderWeight(e.target.value);
                          validateName();
                        }}
                      />
                    </div>
                    <div className="pt-3 bd-highlight">
                      <QESButton qesTestId={`add-specialty__specialty-code__remove-button`} qesType="secondary-danger" onClick={() => handleRemove(lineItem)}>
                        Remove
                      </QESButton>
                    </div>
                  </div>
                ))
              }
            </React.Fragment>
          }
          <React.Fragment>
            <QESSelect
              testId="create-manage-specialtyGroup-panel-content__taxonomyCrosswalks"
              containerClassName="p-2 w-25 bd-highlight"
              className="mb-2"
              label="Taxonomy Crosswalks"
              value={selectedTaxonomyCrosswalk}
              isValid={!!selectedTaxonomyCrosswalk.toString()}
              disabled={!props.allowClientEdit}
              onChange={(e) => {
                setSelectedTaxonomyCrosswalk(e.target.value);
                handleTaxonomyCrosswalkChange(e.target.value);
                validateName();
              }}
            >
              {Object.values(taxonomyCrosswalks).map((x, index) => <option key={index} value={x.key}>{x.props.children}</option>)}
            </QESSelect>
            <div className="d-flex flex-row">
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__taxonomyCode"}
                  containerClassName="mb-2"
                  label="Taxonomy Code"
                  value={taxonomyCode}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                  onChange={(e) => {
                    setTaxonomyCode(e.target.value);
                    validateName();
                  }}
                  disabled={selectedTaxonomyCrosswalk === '' ? true : false}
                  isValid={nameIsValid}
                  errorText={nameIsValidText}
                  wasFocusedExternally={!!props.specialtyGroup}
                />
              </div>
              <div className="w-25 p-2 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__specialtyCode"}
                  containerClassName="mb-2"
                  label="Specialty Code"
                  value={taxonomySpecialtyCode}
                  onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
                  onChange={(e) => {
                    setTaxonomySpecialtyCode(e.target.value);
                    validateName();
                  }}
                  disabled={selectedTaxonomyCrosswalk === '' ? true : false}
                  isValid={nameIsValid}
                  errorText={nameIsValidText}
                  wasFocusedExternally={!!props.specialtyGroup}
                />
              </div>
              <div className="pt-3 align-content-center bd-highlight">
                <QESButton qesTestId={`add-specialty__taxonomySpecialty-code__add-button`} qesType="secondary" onClick={handleTaxonomySpecialtyCodeAdd}>
                  Add
                </QESButton>
              </div>
            </div>
          </React.Fragment>
          {taxonomySpecialtyCodeLineItems?.length > 0 &&
            <React.Fragment>
              {
                taxonomySpecialtyCodeLineItems.map((lineItem) => (
                  <div className="d-flex flex-row" key={lineItem.id}>
                    <div className="w-25 p-2 bd-highlight">
                      <QESTextInput
                        testId={"create-specialty-panel-content__taxonomyCodeLineItem"}
                        containerClassName="mb-2"
                        label=""
                        value={lineItem.taxonomyCode}
                        onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                        onChange={(e) => {
                          setTaxonomyCode(e.target.value);
                          validateName();
                        }}
                      />
                    </div>
                    <div className="w-25 p-2 bd-highlight">
                      <QESTextInput
                        testId={"create-specialty-panel-content__taxonomySpecialtyCodeLineItem"}
                        containerClassName="mb-2"
                        label=""
                        value={lineItem.taxonomySpecialtyCode}
                        onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeAllSpecialCharacters) }}
                        onChange={(e) => {
                          setTaxonomySpecialtyCode(e.target.value);
                          validateName();
                        }}
                      />
                    </div>
                    <div className="pt-3 bd-highlight">
                      <QESButton qesTestId={`add-specialty__taxonomySpecialty-code__remove-button`} qesType="secondary-danger" onClick={() => handleTaxonomySpecialtyCodeRemove(lineItem)}>
                        Remove
                      </QESButton>
                    </div>
                  </div>
                ))
              }
            </React.Fragment>}

          {namedCategories?.length > 1 &&
            <div className="p-1 bd-highlight">
              <QESSelect
                testId="create-manage-specialtyGroup-panel-content__category"
                containerClassName="p-2 w-25 bd-highlight"
                className="mb-2"
                label="Category"
                value={categoryDisplayName}
                onChange={(e) => {
                  handleCategoryChange(e.target.value);
                }}
              >
                {Object.values(namedCategories).map((x, index) => <option key={index} value={x.displayName}>{x.displayName}</option>)}
              </QESSelect>
            </div>
          }

          <div className="d-flex flex-row my-4">
            <div className="w-25 p-2 bd-highlight">
              <p className="qes-text-input__label mb-2">Specialty Details</p>
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__expandable"}
                label="Expandable"
                checked={expandable}
                onChange={(e) => setExpandable(e.target.checked)}
              />
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__Informational"}
                label="Informational"
                checked={informational}
                onChange={(e) => setInformational(e.target.checked)}
              />
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__usingRatios"}
                label="Using Ratios"
                checked={usingRatios}
                onChange={(e) => { handleUsingRatiosCheckboxToggle(e); }}
              />
              <QESCheckbox
                containerClassName="mb-3"
                testId={"create-manage-specialtyGroup-panel-content__telehealthCredit"}
                label="Telehealth Credit"
                checked={telehealthCredit}
                onChange={(e) => {
                  setTelehealthCredit(e.target.checked);
                  updateCreditPercentage(e.target.checked);
                }}
              />

              <QESTextInput
                testId={"create-specialty-panel-content__creditPercentage"}
                className="col-4"
                containerClassName="mb-2 ms-4"
                label="Credit Percentage"
                value={creditPercentage}
                disabled={!telehealthCredit}
                onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100); }}
                onChange={(e) => { handleCreditPercentageChange(e) }}
                isValid={creditPercentageIsValid}
                errorText={"Telehealth credit Percentage cannot be higher than 100"}
                unrequiredValidation={true}
              />
            </div>
          </div>

          <div className="ms-2 mb-5">
            <p className="qes-text-input__label">Provider or Facility Option?</p>
            <QESRadioButton
              label="Provider"
              value="provider"
              radioContainerClassName="mb-3"
              checked={isProvider}
              onChange={() => { setIsProvider(true); }}
              testId={`create-specialty-panel-content__isProviderRadio`}
            />
            <QESRadioButton
              label="Facility"
              value="facility"
              checked={!isProvider}
              onChange={() => { setIsProvider(false); }}
              testId={`create-specialty-panel-content__isFacilityRadio`}
            />
          </div>

          <div className="ms-2 mb-5">
            <p className="qes-text-input__label">Access Requirements</p>
            <QESCheckbox
              containerClassName="mb-3"
              testId={"create-manage-specialtyGroup-panel-content__skipAccessTimeAndDistanceCalc"}
              label="Skip Access Time and Distance"
              checked={skipAccessTimeAndDistanceCalc}
              onChange={(e) => handleSkipAccessCheckboxToggle(e)}
            />
            <QESCheckbox
              containerClassName="mb-3"
              testId={"create-manage-specialtyGroup-panel-content__accessRequirements"}
              label="In-County Requirements"
              checked={accessReqInCounty}
              onChange={(e) => handleInCountyRequirementsCheckboxToggle(e)}
            />
          </div>
          <div className="ms-2 mb-5">
            <p className="qes-text-input__label">Servicing Requirements</p>
            <QESCheckbox
              containerClassName="mb-4"
              testId={"create-manage-specialtyGroup-panel-content__servicingRequirements"}
              label="In-County Requirements"
              checked={servicingReqInCounty}
              onChange={(e) => setServicingReqInCounty(e.target.checked)}
            />
          </div>
          <div className="ms-2 me-4 p-2 border">
            <div className="header-s pt-2">
              Servicing Details
            </div>
            {/* RatiosAndCountsTable */}
            <div className="d-flex flex-row my-4">
              <div className="p-1 bd-highlight">
                <QESTextInput
                  testId={"create-specialty-panel-content__providerCount"}
                  containerClassName="mb-1 label-s"
                  label={
                    <TooltipWrapper text={"This field auto populates the Required Providers column in the table below"} position="top">
                      <i className={"fa fa-info-circle waffles-tooltip-indicator"} /> Provider Count
                    </TooltipWrapper>
                  }
                  value={providerCount ? providerCount : 0}
                  onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2, false, true); }}
                  onChange={(e) => { handleProviderCountChange(e.target.value); }}
                />
              </div>
              <div className={"p-1 bd-highlight" + (usingRatios ? " col-3" : "")}>
                <div className="d-flex flex-column">
                  <label className="qes-text-input__label" htmlFor="create-specialty-panel-content__minimumServicingCounts">
                    <TooltipWrapper text={"This field auto populates the Servicing Providers column in the table below"} position="top">
                      <i className={"fa fa-info-circle waffles-tooltip-indicator"} /> Minimum Servicing Counts
                    </TooltipWrapper>
                  </label>
                  <div className="d-flex flex-row">
                    {usingRatios === false &&
                      <QESTextInput
                        testId={"create-specialty-panel-content__minimumServicingCounts"}
                        containerClassName={"mb-1 me-1 label-s"}
                        label=""
                        hideLabel={true}
                        value={minimumServicingCounts ? minimumServicingCounts : 0}
                        onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsNoLimit, false, e.currentTarget.value === "0"); }}
                        onChange={(e) => {
                          handleMinimumServicingCountsChange(e.target.value);
                        }}
                      />
                    }
                    {usingRatios === true &&
                      <>
                        <QESTextInput
                          testId={"create-specialty-panel-content__minimumServicingCounts"}
                          containerClassName={"mb-1 me-1 label-s"}
                          className={usingRatios && +minimumServicingCounts > +memberCounts ? "border border-danger" : ""}
                          label=""
                          hideLabel={true}
                          isValid={minimumServicingCounts <= memberCounts}
                          errorText={"Servicing Minimum Count must be less than Member Count"}
                          value={minimumServicingCounts ? minimumServicingCounts : 0}
                          onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                          onChange={(e) => {
                            handleMinimumServicingCountsChange(e.target.value);
                          }}
                        />
                        <QESTextInput
                          testId={"create-specialty-panel-content__memberCounts"}
                          containerClassName="mb-1 label-s"
                          className={+memberCounts < +minimumServicingCounts ? "border border-danger" : ""}
                          label=""
                          hideLabel={true}
                          isValid={minimumServicingCounts <= memberCounts}
                          errorText={"Member Count must be more than Servicing Minimum Count"}
                          value={memberCounts ? memberCounts : 0}
                          onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowDigitsAndPeriod, true); }}
                          onChange={(e) => {
                            handleMemberCountsChange(e.target.value);
                          }}
                        />
                      </>
                    }
                  </div>
                </div>
              </div>
              <div className="p-1 bd-highlight">
                <QESSelect
                  testId="create-manage-specialtyGroup-panel-content__countDistinct"
                  containerClassName="mb-1 ms-1 label-s"
                  label="Count Distinct"
                  className={(countDistinct === "unknown" || countDistinct.length === 0) && highlightErrors ? "border border-danger" : ""}
                  errorText={"Count Distinct must be selected"}
                  value={countDistinct}
                  required={true}
                  isValid={countDistinct.length != 0 && countDistinct != "unknown"}
                  onChange={(e) => {
                    setCountDistinct(e.target.value as ServicingCountTypeEnum);
                  }}
                >
                  {Object.values(ServicingCountTypeEnum).filter(x => x !== ServicingCountTypeEnum.Unknown).map((x, index) => <option key={index} value={x}>{x}</option>)}
                </QESSelect>
              </div>
            </div>
            {servicingDetailsErrorMessages.length > 0 && highlightErrors && (
              <div className="p-1 bd-highlight my-2">
                {getErrorMessages()}
              </div>
            )}
            <div className="header-s my-4">
              Template Counts
            </div>
            {/* Waffles Servicing Details */} {servicingDetailsData.length > 0 &&
              <div className="d-flex flex-row">
                <div className="p-1 bd-highlight">
                  <Waffles<ServicingDetailsRow>
                    columns={skipAccessTimeAndDistanceCalc === true ? classificationSkipAccessCheckedColumnConfig : classificationColumnConfig}
                    data={servicingDetailsData}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    testId="client-manage-specialtyGroupServicingDetails-waffles-table" />
                </div>
              </div>}
            <div>
              <QESButton
                qesTestId={`add-specialty__access-customization__add-button`}
                qesType="secondary"
                onClick={handleAddAccessCustomization}
                disabled={accessCustomizations.length > 0 &&
                  !accessCustomizations.map((x) => x.geographyId).every((x) => !!x)}
              >
                Add Access Customization
              </QESButton>
            </div> {accessCustomizationsData.length > 0 &&
              <div className="d-flex flex-row">
                <div className="p-1 bd-highlight">
                  <Waffles<ServicingDetailsRow>
                    columns={skipAccessTimeAndDistanceCalc === true ? customizationSkipAccessCheckedColumnConfig : customizationColumnConfig}
                    data={accessCustomizationsData}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    testId="client-manage-specialtyGroupAccessCustomization-waffles-table" />
                </div>
              </div>}
          </div>

          <FlyoutFooter
            submitButton={<QESButton
              qesTestId="create-manage-specialtyGroup-panel-content__confirm"
              className="mt10"
              onClick={handleAddClick}
            >Save Specialty</QESButton>
            }
            cancelButtonText={"Cancel"}
          />
        </>
      }
    </>
  );
}

export default CreateNewSpecialtyPanelContent;
